/**
 * Camera RAW files: no browser decodes them and neither does the server, but
 * the TIFF-based ones (Canon CR2, Nikon NEF, Sony ARW, DNG…) carry a JPEG of
 * the shot inside, the one the camera's own screen shows. That JPEG is what
 * gets uploaded — the 25–30 MB RAW never leaves the phone.
 *
 * Not covered: CR3 and Fuji RAF aren't TIFF, so they keep the "export it as
 * JPEG" message.
 */

const RAW_EXTENSION = /\.(cr2|nef|nrw|arw|srf|sr2|dng|pef|srw|erf|3fr|mef|mos|kdc|dcr)$/i;

export function isRawPhoto(file: File): boolean {
  return RAW_EXTENSION.test(file.name);
}

export interface RawPreviewLocation {
  offset: number;
  length: number;
  /** The RAW's EXIF orientation, 1 when absent: the embedded JPEG rarely has its own. */
  orientation: number;
}

const TAG = {
  compression: 0x0103,
  stripOffsets: 0x0111,
  orientation: 0x0112,
  stripByteCounts: 0x0117,
  subIfds: 0x014a,
  jpegOffset: 0x0201,
  jpegLength: 0x0202,
} as const;

const TYPE_SIZE: Record<number, number> = { 3: 2, 4: 4, 13: 4 };
const MAX_IFDS = 32;

/** Baseline, extended and progressive: what a browser can draw. */
const DRAWABLE_SOF = new Set([0xc0, 0xc1, 0xc2]);
/** The rest of the SOF markers: lossless (the raw sensor data itself in CR2/DNG), arithmetic. */
const OTHER_SOF = new Set([0xc3, 0xc5, 0xc6, 0xc7, 0xc9, 0xca, 0xcb, 0xcd, 0xce, 0xcf]);

/**
 * A RAW also stores its sensor data as "JPEG" (lossless, SOF3), and it is the
 * biggest one — so a candidate counts only if its frame is one a browser draws.
 */
export function isDrawableJpeg(view: DataView, start: number, end: number): boolean {
  if (end - start < 4 || view.getUint16(start) !== 0xffd8) return false;
  let pos = start + 2;
  while (pos + 4 <= end) {
    if (view.getUint8(pos) !== 0xff) return false;
    const marker = view.getUint8(pos + 1);
    if (marker === 0xff) { pos += 1; continue; }
    if (DRAWABLE_SOF.has(marker)) return true;
    if (OTHER_SOF.has(marker) || marker === 0xda || marker === 0xd9) return false;
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) { pos += 2; continue; }
    pos += 2 + view.getUint16(pos + 2);
  }
  return false;
}

/** The biggest drawable JPEG embedded in a TIFF-based RAW, or null. */
export function findRawPreview(buffer: ArrayBuffer): RawPreviewLocation | null {
  const view = new DataView(buffer);
  if (buffer.byteLength < 8) return null;
  const order = view.getUint16(0);
  if (order !== 0x4949 && order !== 0x4d4d) return null;
  const le = order === 0x4949;
  if (view.getUint16(2, le) !== 42) return null;

  const inBounds = (offset: number, length = 0) => offset >= 0 && offset + length <= buffer.byteLength;

  function values(entry: number): number[] {
    const type = view.getUint16(entry + 2, le);
    const count = view.getUint32(entry + 4, le);
    const size = TYPE_SIZE[type];
    if (!size || count === 0 || count > 1024) return [];
    const at = size * count <= 4 ? entry + 8 : view.getUint32(entry + 8, le);
    if (!inBounds(at, size * count)) return [];
    return Array.from({ length: count }, (_, i) =>
      size === 2 ? view.getUint16(at + i * 2, le) : view.getUint32(at + i * 4, le));
  }

  let orientation = 1;
  const best = { offset: 0, length: 0 };
  const consider = (offset: number | undefined, length: number | undefined) => {
    if (offset === undefined || !length || length <= best.length || !inBounds(offset, length)) return;
    if (isDrawableJpeg(view, offset, offset + length)) Object.assign(best, { offset, length });
  };

  const queue = [view.getUint32(4, le)];
  const seen = new Set<number>();
  while (queue.length > 0 && seen.size < MAX_IFDS) {
    const ifd = queue.shift()!;
    if (ifd === 0 || seen.has(ifd) || !inBounds(ifd, 2)) continue;
    const isFirst = seen.size === 0;
    seen.add(ifd);
    const count = view.getUint16(ifd, le);
    if (!inBounds(ifd + 2, count * 12 + 4)) continue;

    const tags = new Map<number, number[]>();
    for (let i = 0; i < count; i++) {
      const entry = ifd + 2 + i * 12;
      tags.set(view.getUint16(entry, le), values(entry));
    }
    if (isFirst) orientation = tags.get(TAG.orientation)?.[0] ?? 1;

    consider(tags.get(TAG.jpegOffset)?.[0], tags.get(TAG.jpegLength)?.[0]);
    const strips = tags.get(TAG.stripOffsets);
    const stripLengths = tags.get(TAG.stripByteCounts);
    const compression = tags.get(TAG.compression)?.[0];
    if ((compression === 6 || compression === 7) && strips?.length === 1) consider(strips[0], stripLengths?.[0]);

    queue.push(...(tags.get(TAG.subIfds) ?? []), view.getUint32(ifd + 2 + count * 12, le));
  }

  return best.length > 0 ? { ...best, orientation } : null;
}

export async function extractRawPreview(file: File): Promise<{ jpeg: Blob; orientation: number } | null> {
  const location = findRawPreview(await file.arrayBuffer());
  if (!location) return null;
  const { offset, length, orientation } = location;
  return { jpeg: file.slice(offset, offset + length, 'image/jpeg'), orientation };
}
